import { useEffect } from 'react';

/**
 * Dynamic per-route SEO manager.
 * Sets: title, meta description, keywords, canonical, robots, og, twitter, hreflang,
 * and injects a page-specific JSON-LD @graph block.
 */
export function useSEO({
  title,
  description,
  keywords,
  canonicalPath,
  hreflang = [],
  jsonLd = null,
} = {}) {
  useEffect(() => {
    const origin = 'https://www.firstpageconsultants.com';
    const canonical = canonicalPath ? `${origin}${canonicalPath}` : origin + '/';

    if (title) document.title = title;

    const setMeta = (attr, key, value) => {
      if (!value) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', value);
    };

    setMeta('name', 'description', description);
    setMeta('name', 'keywords', keywords);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', canonical);
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);

    // Canonical
    let canonicalLink = document.head.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonical);

    // Hreflang links (marked so we can clean up on route change)
    document.head
      .querySelectorAll('link[data-dyn-hreflang="true"]')
      .forEach((n) => n.remove());
    hreflang.forEach(({ lang, href }) => {
      const l = document.createElement('link');
      l.setAttribute('rel', 'alternate');
      l.setAttribute('hreflang', lang);
      l.setAttribute('href', href.startsWith('http') ? href : `${origin}${href}`);
      l.setAttribute('data-dyn-hreflang', 'true');
      document.head.appendChild(l);
    });

    // JSON-LD (page-specific) — cleaned on unmount
    let jsonScript = null;
    if (jsonLd) {
      jsonScript = document.createElement('script');
      jsonScript.type = 'application/ld+json';
      jsonScript.dataset.dynSeo = 'true';
      jsonScript.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(jsonScript);
    }

    return () => {
      if (jsonScript) jsonScript.remove();
    };
  }, [title, description, keywords, canonicalPath, JSON.stringify(hreflang), JSON.stringify(jsonLd)]);
}
